import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, GraduationCap } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

export default function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([
        api.get("/users", { params: { role: "student" } }),
        api.get("/classes"),
      ]);
      setStudents(s.data);
      setClasses(c.data);
    })();
  }, []);

  const classMap = Object.fromEntries(classes.map((c) => [c.id, c.name]));
  const filtered = students.filter((s) =>
    (s.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (s.email || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Roster</div>
          <h1 className="text-4xl font-bold tracking-tight">Students</h1>
          <p className="text-muted-foreground text-sm mt-1">{students.length} learners across {classes.length} classes.</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="students-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search learners…" className="pl-10 rounded-2xl h-11" />
        </div>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-base">All learners</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-border/60">
            {filtered.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No learners found.</div>}
            {filtered.map((s) => (
              <div key={s.id} className="py-3 flex items-center gap-4" data-testid={`student-row-${s.id}`}>
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary/15 text-primary text-xs">{initials(s.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                </div>
                <Badge variant="outline" className="rounded-full">
                  <GraduationCap className="h-3 w-3 mr-1" /> Grade {s.grade || "—"}
                </Badge>
                <Badge className="rounded-full bg-muted text-foreground hover:bg-muted">
                  {classMap[s.class_id] || "Unassigned"}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
